import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import SEO from "../../components/common/SEO";
import {
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Brain,
  Upload,
  Download,
  Zap,
  Shield,
  TrendingUp,
  Activity,
  Sparkles,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export default function BulkValidation({ user, authToken }) {
  const [activeTab, setActiveTab] = useState("bulk"); // bulk or upload
  const [bulkEmails, setBulkEmails] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [includeAI, setIncludeAI] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const isAuthenticated = Boolean(authToken);

  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    [authToken]
  );

  const validateBulkEmails = async () => {
    const emails = bulkEmails
      .split("\n")
      .map((email) => email.trim())
      .filter((email) => email);

    if (emails.length === 0) {
      toast.error("Please enter at least one email address");
      return;
    }

    if (emails.length > 1000) {
      toast.error("Maximum 1000 emails per batch");
      return;
    }

    if (!isAuthenticated) {
      toast.error("Please sign in to validate emails");
      window.location.href = "/signin";
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const response = await axios.post(
        `${API}/validate-bulk`,
        {
          emails,
          include_ai_analysis: includeAI,
        },
        { headers: authHeaders }
      );
      setResults(response.data);
      toast.success(`Successfully validated ${emails.length} emails!`);
    } catch (error) {
      console.error("Bulk validation failed:", error);
      const message = error?.response?.data?.detail || "Bulk validation failed";
      toast.error(message);

      if (error?.response?.status === 401) {
        window.location.href = "/signin";
      } else if (error?.response?.status === 402) {
        window.location.href = "/pricing";
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    if (!isAuthenticated) {
      toast.error("Please sign in to upload files");
      window.location.href = "/signin";
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setLoading(true);
    setResults(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload-csv`, formData, {
        headers: {
          ...authHeaders,
          "Content-Type": "multipart/form-data",
        },
      });
      setResults(response.data.validation_results || response.data);
      toast.success("File validated successfully!");
    } catch (error) {
      console.error("File upload failed:", error);
      const message = error?.response?.data?.detail || "File upload failed";
      toast.error(message);

      if (error?.response?.status === 401) {
        window.location.href = "/signin";
      } else if (error?.response?.status === 402) {
        window.location.href = "/pricing";
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!results || !results.results) return;

    const csvRows = [];
    csvRows.push([
      "Email",
      "Valid",
      "Risk Level",
      "Confidence",
      "Syntax Valid",
      "DNS Valid",
      "Domain",
      "Disposable",
    ]);

    results.results.forEach((result) => {
      csvRows.push([
        result.email,
        result.is_valid ? "Yes" : "No",
        result.risk_level,
        Math.round(result.confidence_score * 100) + "%",
        result.validation_details?.syntax_valid ? "Yes" : "No",
        result.validation_details?.dns_valid ? "Yes" : "No",
        result.validation_details?.domain || "",
        result.validation_details?.is_disposable ? "Yes" : "No",
      ]);
    });

    const csvContent = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `validation-results-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case "low":
        return "text-green-600 dark:text-green-400";
      case "medium":
        return "text-yellow-600 dark:text-yellow-400";
      case "high":
        return "text-red-600 dark:text-red-400";
      default:
        return "text-slate-600 dark:text-slate-400";
    }
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel) {
      case "low":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "medium":
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case "high":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-slate-600" />;
    }
  };

  return (
    <>
      <SEO
        title="Bulk Email Validation - VALIDMAIL"
        description="Validate thousands of emails at once. Upload CSV files or paste email lists. Get comprehensive reports with deliverability scoring and AI analysis."
        keywords="bulk email validation, email list cleaning, CSV validation, bulk email checker"
        canonicalUrl="https://validmail.io/product/bulk-validation"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-sm font-medium mb-6">
              <Sparkles className="h-4 w-4" />
              Bulk List Cleaning
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-6">
              Clean email lists{" "}
              <span className="text-purple-600 dark:text-purple-400">
                at scale
              </span>
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Validate thousands of emails in seconds. Upload CSV files or paste
              lists directly. Get comprehensive reports with detailed analytics.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6 border border-purple-200 dark:border-purple-800">
              <Zap className="h-10 w-10 text-purple-600 dark:text-purple-400 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                Lightning Fast
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Process 1000+ emails in under 30 seconds with parallel validation
              </p>
            </div>

            <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-2xl p-6 border border-sky-200 dark:border-sky-800">
              <Upload className="h-10 w-10 text-sky-600 dark:text-sky-400 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                CSV Upload
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Drag & drop CSV, XLSX, or XLS files for instant validation
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-800">
              <Download className="h-10 w-10 text-emerald-600 dark:text-emerald-400 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                Export Results
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Download detailed CSV reports with all validation metrics
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800">
              <Activity className="h-10 w-10 text-amber-600 dark:text-amber-400 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                Analytics
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Comprehensive insights with risk distribution and domain stats
              </p>
            </div>
          </div>

          {/* Validation Interface */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border-2 border-slate-200 dark:border-slate-800 p-8">
              {/* AI Toggle */}
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  <div>
                    <span className="font-semibold text-slate-900 dark:text-slate-50 block">
                      AI-Powered Security Analysis
                    </span>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {includeAI
                        ? "Enhanced analysis with threat detection & insights"
                        : "Basic validation only - no AI security analysis"}
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAI}
                    onChange={(e) => setIncludeAI(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-900 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              {/* Tab Navigation */}
              <div className="flex gap-2 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 mb-8">
                <button
                  onClick={() => setActiveTab("bulk")}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition-all ${activeTab === "bulk"
                      ? "bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-400 shadow-md"
                      : "text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400"
                    }`}
                >
                  <FileText className="w-5 h-5" />
                  Paste Emails
                </button>
                <button
                  onClick={() => setActiveTab("upload")}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition-all ${activeTab === "upload"
                      ? "bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-400 shadow-md"
                      : "text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400"
                    }`}
                >
                  <Upload className="w-5 h-5" />
                  Upload File
                </button>
              </div>

              {/* Bulk Emails Tab */}
              {activeTab === "bulk" && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                    Email Addresses (one per line)
                  </label>
                  <textarea
                    value={bulkEmails}
                    onChange={(e) => setBulkEmails(e.target.value)}
                    rows={12}
                    placeholder="example1@domain.com&#10;example2@domain.com&#10;example3@domain.com"
                    className="w-full px-4 py-3 border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-50 rounded-xl focus:ring-4 focus:ring-purple-300 dark:focus:ring-purple-900 focus:border-purple-500 dark:focus:border-purple-400 transition-all font-mono text-sm"
                  />
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                    Maximum 1000 emails per batch
                  </p>

                  <button
                    onClick={validateBulkEmails}
                    disabled={loading || !bulkEmails.trim()}
                    className="mt-6 w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-4 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        <span>Processing Batch...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        <span>Validate Email List</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* File Upload Tab */}
              {activeTab === "upload" && (
                <div>
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-12 text-center hover:border-purple-500 dark:hover:border-purple-400 transition-all bg-slate-50 dark:bg-slate-800/50">
                    <Upload className="w-16 h-16 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
                    <p className="text-slate-700 dark:text-slate-300 font-semibold mb-2">
                      Drag & drop your file here
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                      or click to browse
                    </p>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      id="file-upload"
                      disabled={loading}
                    />
                    <label
                      htmlFor="file-upload"
                      className={`inline-block px-8 py-3 rounded-xl font-semibold transition-all ${loading
                          ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                          : "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 cursor-pointer shadow-lg hover:shadow-xl"
                        }`}
                    >
                      {loading ? "Uploading..." : "Choose File"}
                    </label>

                    {uploadedFile && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-4">
                        Selected: <span className="font-semibold">{uploadedFile.name}</span>
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-4 text-center">
                    Supported formats: CSV, XLSX, XLS • Maximum 1000 emails
                  </p>
                </div>
              )}

              {/* Credits Info */}
              {isAuthenticated && user && (
                <div className="mt-6 flex items-center justify-between text-sm text-slate-500 dark:text-slate-400 pt-6 border-t border-slate-200 dark:border-slate-800">
                  <span>Available credits: {user.credits || 0}</span>
                  <span>Cost: 1 credit per email</span>
                </div>
              )}
            </div>

            {/* Results Display */}
            {results && (
              <div className="mt-8 bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border-2 border-slate-200 dark:border-slate-800 p-8">
                {/* Results Header */}
                <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-200 dark:border-slate-800">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                      Validation Results
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Processed in {results.processing_time?.toFixed(2) || "N/A"}s
                    </p>
                  </div>
                  <button
                    onClick={downloadCSV}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    <Download className="w-5 h-5" />
                    Export CSV
                  </button>
                </div>

                {/* Summary Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 text-center border border-blue-200 dark:border-blue-800">
                    <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {results.summary?.total_processed || results.results?.length}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                      Total Processed
                    </div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 text-center border border-green-200 dark:border-green-800">
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {results.summary?.valid_count ||
                        results.results?.filter((r) => r.is_valid).length}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                      Valid Emails
                    </div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 text-center border border-red-200 dark:border-red-800">
                    <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                      {results.summary?.invalid_count ||
                        results.results?.filter((r) => !r.is_valid).length}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                      Invalid Emails
                    </div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 text-center border border-purple-200 dark:border-purple-800">
                    <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                      {results.summary?.validation_rate ||
                        Math.round(
                          (results.results?.filter((r) => r.is_valid).length /
                            results.results?.length) *
                          100
                        )}
                      %
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                      Success Rate
                    </div>
                  </div>
                </div>

                {/* Risk Distribution */}
                {results.summary?.risk_distribution && (
                  <div className="mb-8">
                    <h4 className="font-bold text-slate-900 dark:text-slate-50 mb-4">
                      Risk Distribution
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(results.summary.risk_distribution).map(
                        ([risk, count]) => (
                          <div
                            key={risk}
                            className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-xl p-4"
                          >
                            <div className="flex items-center gap-2">
                              {getRiskIcon(risk)}
                              <span className="font-medium capitalize text-slate-900 dark:text-slate-50">
                                {risk}
                              </span>
                            </div>
                            <span className="font-bold text-slate-900 dark:text-slate-50">
                              {count}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {results.ai_insights && (
                  <div className="mb-8 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-xl p-6">

                    <div className="flex items-center gap-3 mb-3">
                      <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                      <h4 className="font-bold text-purple-900 dark:text-purple-300">
                        AI Insights
                      </h4>
                    </div>

                    {/* Styled wrapper for prose (react-markdown v9 cannot take className) */}
                    <div
                      className="prose dark:prose-invert max-w-none
                 prose-h3:text-purple-700 dark:prose-h3:text-purple-300
                 prose-strong:text-slate-900 dark:prose-strong:text-white
                 prose-li:marker:text-purple-600 dark:prose-li:marker:text-purple-400
                 prose-code:bg-slate-200 dark:prose-code:bg-slate-800
                 prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      >
                        {results.ai_insights}
                      </ReactMarkdown>
                    </div>

                  </div>
                )}


                {/* Individual Results Table */}
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-slate-50 mb-4">
                    Individual Results
                  </h4>
                  <div className="max-h-96 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-800">
                    <div className="space-y-2 p-4">
                      {results.results?.map((result, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg p-4 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {result.is_valid ? (
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            )}
                            <span className="font-mono text-sm text-slate-900 dark:text-slate-50 truncate">
                              {result.email}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span
                              className={`text-sm font-semibold capitalize ${getRiskColor(
                                result.risk_level
                              )}`}
                            >
                              {result.risk_level}
                            </span>
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                              {Math.round(result.confidence_score * 100)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CTA Section */}
          {!isAuthenticated && (
            <div className="mt-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-12 text-center text-white shadow-2xl">
              <h2 className="text-3xl font-bold mb-4">
                Clean your email lists today
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Start with 100 free credits. No credit card required.
              </p>
              <button
                onClick={() => (window.location.href = "/signin")}
                className="px-8 py-4 rounded-full text-lg font-bold bg-white text-purple-600 hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform duration-200"
              >
                Sign Up Free
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
